/**
 * RP → SP3 conversion (1:1, level quota).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { usersAPI } from '../api/users';
import { useAuthStore } from '../store/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
  ThemedTextarea,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';

function fmtNum(v: string | number | undefined, digits = 2): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export function RpToSp3Page() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [amount, setAmount] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [memo, setMemo] = useState('');
  const [showSecurity, setShowSecurity] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const previewAmount = Number(amount) > 0 ? amount.trim() : '1';
  const { data: preview } = useQuery({
    queryKey: ['users', 'convert', 'rp-sp3', 'preview', previewAmount],
    queryFn: () => usersAPI.convertRpSp3Preview({ amount: previewAmount }),
    refetchInterval: 30_000,
  });

  const convertMut = useMutation({
    mutationFn: () =>
      usersAPI.convertRpSp3({
        amount: amount.trim(),
        transaction_password: txPassword.trim(),
        security_answer: securityAnswer.trim(),
        memo: memo.trim() || undefined,
      }),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ['user'] });
      qc.invalidateQueries({ queryKey: ['users', 'convert', 'rp-sp3'] });
      setTimeout(() => navigate(-1), 1200);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('rpSp3.failed', 'RP转SP3失败'));
    },
  });

  const rpBalance = String(preview?.rp_balance ?? '0');
  const sp3Balance = String(preview?.sp3_balance ?? '0');
  const quotaRemaining = String(preview?.quota_remaining ?? '0');
  const quotaTotal = String(preview?.quota_total ?? '0');
  const levelCode = String(preview?.level_code ?? profile?.level_code ?? '—');

  const securityQuestion =
    ((profile as Record<string, unknown> | null)?.security_question as string | undefined) ??
    t('convert.defaultSecurityQuestion');

  const formDisabled =
    !amount.trim() ||
    Number(amount) <= 0 ||
    !txPassword.trim() ||
    (showSecurity && !securityAnswer.trim()) ||
    convertMut.isPending ||
    success;

  function handleSubmit() {
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError(t('rpSp3.amountError', '请输入有效数量'));
      return;
    }
    if (Number(amount) > Number(rpBalance)) {
      setError(t('rpSp3.insufficientRp', 'RP 余额不足'));
      return;
    }
    if (Number(amount) > Number(quotaRemaining)) {
      setError(
        t('rpSp3.quotaExceeded', '超过本级可转额度（剩余 {{remain}}）', {
          remain: quotaRemaining,
        }),
      );
      return;
    }
    convertMut.mutate();
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t('rpSp3.title', 'RP转SP3')}
          onBack={() => navigate(-1)}
          theme={C}
        />
        <div
          className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]"
          style={{ height: 'calc(100% - 70px)', paddingBottom: '88px' }}
        >
          <ThemedPanel className="overflow-hidden p-0">
            <div className="relative px-4 py-4">
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                  background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)`,
                }}
              />
              <div
                className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border px-3 py-3"
                style={{ borderColor: C.softBorder, background: C.inputBg }}
              >
                <div className="min-w-0">
                  <div className="truncate text-[10px] leading-none" style={{ color: C.muted }}>
                    {t('convert.holding')} RP
                  </div>
                  <div
                    className="mt-2 truncate font-mono text-xl font-semibold leading-none"
                    style={{ color: C.gold }}
                  >
                    {fmtNum(rpBalance)}
                  </div>
                </div>
                <div className="h-11 w-px" style={{ background: C.statDividerAlt }} />
                <div className="min-w-0 text-right">
                  <div className="truncate text-[10px] leading-none" style={{ color: C.muted }}>
                    SP3 {t('convert.balance')}
                  </div>
                  <div
                    className="mt-2 truncate font-mono text-xl font-semibold leading-none"
                    style={{ color: C.text }}
                  >
                    {fmtNum(sp3Balance)}
                  </div>
                </div>
              </div>
              <div
                className="mt-3 space-y-1 rounded-xl border px-3 py-2 text-xs"
                style={{
                  borderColor: C.line,
                  background: `linear-gradient(90deg, transparent 0%, ${C.gold}10 100%)`,
                  color: C.muted,
                }}
              >
                <div className="flex justify-between">
                  <span>{t('rpSp3.ratio', '兑换比例')}</span>
                  <span className="font-mono font-semibold" style={{ color: C.gold }}>
                    1:1
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('rpSp3.level', '投资星级')}</span>
                  <span className="font-mono font-semibold" style={{ color: C.gold }}>
                    {levelCode}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>{t('rpSp3.quota', '可转额度')}</span>
                  <span className="font-mono font-semibold" style={{ color: C.gold }}>
                    {fmtNum(quotaRemaining)} / {fmtNum(quotaTotal)}
                  </span>
                </div>
              </div>
            </div>
          </ThemedPanel>

          <ThemedNotice tone="info">
            {t(
              'rpSp3.hint',
              'RP→SP3 受投资星级额度限制；需交易密码与密保答案。',
            )}
          </ThemedNotice>

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <ThemedField label={t('convert.amount')} required>
                <div className="relative">
                  <ThemedInput
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={t('convert.placeholder')}
                    min="0"
                    step="any"
                    className="pr-16"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setAmount(
                        String(
                          Math.min(Number(rpBalance) || 0, Number(quotaRemaining) || 0),
                        ),
                      )
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold"
                    style={{ color: C.gold }}
                  >
                    {t('common.max', '最大')}
                  </button>
                </div>
              </ThemedField>

              <ThemedField label={t('convert.memo')}>
                <ThemedTextarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  rows={2}
                  placeholder={t('convert.memoPlaceholder')}
                />
              </ThemedField>

              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={securityQuestion}
                disabled={convertMut.isPending || success}
              />
            </div>
          </ThemedPanel>

          {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}
          {success ? (
            <ThemedNotice tone="success">{t('rpSp3.success', 'RP转SP3成功')}</ThemedNotice>
          ) : null}
        </div>

        <ThemedBottomActionBar>
          <ThemedSubmitButton disabled={formDisabled} onClick={() => void handleSubmit()}>
            {t('rpSp3.submit', '确认转换')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

export default RpToSp3Page;
